import React, {CSSProperties, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Popup} from '@components/common/base/popup'
import {useGlobalKeyDown} from '@containers/hooks'
import {CheckboxNew} from '../checkbox-new/checkbox-new'
import {TwDialogSubtitle} from './dialog-containers'
import {DialogFooter} from './dialog-footer'

export interface DialogProps {
  submit: () => void
  close: () => void
  cancel?: () => void
  type?: 'two-buttons' | 'one-button'

  title: string
  subtitle?: string
  width?: number
  children?: React.ReactNode
  inlineDisplay?: boolean

  isDelete?: boolean
  primaryBtnText?: string
  closeBtnText?: string
  primaryBtnDisabled?: boolean
  skipSubmitBySpace?: boolean
  popupInWnd?: boolean
  noScroll?: boolean
  hidePrimaryBtn?: boolean
  headerStyle?: CSSProperties

  noShowMore?: boolean
  noShowMoreDefaultValue?: boolean
  noShowMoreText?: string
  noShowMoreTextCb?: (value: boolean) => void

  footer?: JSX.Element
  autoHeight?: boolean
  height?: string
  noCross?: boolean
}

export const Dialog = (props: DialogProps) => {
  const {
    submit,
    close,
    title,
    subtitle,
    width = 360,
    children,
    isDelete,
    headerStyle
  } = props
  const {
    noShowMore,
    noShowMoreDefaultValue,
    noShowMoreText = Lang.msg.common.dontShowMessageAgain,
    noShowMoreTextCb
  } = props
  const {
    primaryBtnText,
    closeBtnText,
    primaryBtnDisabled,
    popupInWnd,
    type = 'two-buttons',
    inlineDisplay,
    hidePrimaryBtn,
    height,
    noCross = true
  } = props
  const {noScroll = true} = props

  const [showMoreValue, setShowMoreValue] = useState(!!noShowMoreDefaultValue)

  const cancel = props.cancel ?? close

  const onChangeShowMoreValue = (value: boolean) => {
    setShowMoreValue(value)
    noShowMoreTextCb?.(value)
  }

  useGlobalKeyDown({
    escape: () => {
      close()
      return true
    },
    anyKey: e => {
      if (e.key === 'Enter' && !e.shift) {
        submit()
        return true
      }

      if (!props.skipSubmitBySpace) {
        if (e.key === ' ' || e.code === 'Space') {
          submit()
          return true
        }
      }
      return false
    }
  })

  const footer = props.footer ?? (
    <DialogFooter
      close={cancel}
      submit={submit}
      primaryBtnText={primaryBtnText}
      closeBtnText={closeBtnText}
      primaryBtnDisabled={primaryBtnDisabled}
      isDelete={isDelete}
      type={type}
      hidePrimaryBtn={hidePrimaryBtn}
    />
  )

  return (
    <Popup
      minHeightAuto
      inlineDisplay={inlineDisplay}
      close={close}
      title={title}
      width={width}
      autoHeight={height ? false : props.autoHeight ?? true}
      height={height}
      footer={footer}
      noScroll={noScroll}
      type={'dialog'}
      rounded={'16'}
      popupInWnd={popupInWnd}
      headerStyle={headerStyle}
      noCross={noCross}
    >
      {subtitle && <TwDialogSubtitle>{subtitle}</TwDialogSubtitle>}
      {children}
      {noShowMore && (
        <CheckboxNew
          label={noShowMoreText}
          value={showMoreValue}
          onChange={onChangeShowMoreValue}
          labelClassName="text-secondary"
          className="mt-8"
        />
      )}
    </Popup>
  )
}
